import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Row,
  Button,
  Input,
  Spinner,
} from 'reactstrap';
import BreadCrumb from '../../components/Common/BreadCrumb';
import PromotionTable from './PromotionTable';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import Flatpickr from 'react-flatpickr';

const promotionStatusOptions = [
  { value: '', label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'updated', label: 'Updated' },
  { value: 'created', label: 'Created' },
];

const promotionTagsOptions = [
  { value: '', label: 'All' },
  { value: 'Points', label: 'Points' },
  { value: 'Transactional', label: 'Transactional' },
];

const countryOptions = [
  { value: '', label: 'All' },
  { value: 'AE', label: 'UAE' },
  { value: 'BH', label: 'Bahrain' },
  { value: 'OM', label: 'Oman' },
  { value: 'QA', label: 'Qatar' },
];

const sortByOptions = [
  {
    label: 'Default',
    value: null,
  },
  {
    label: 'Start Date (asc)',
    value: {
      key: 'launcher.startDate',
      order: 'asc',
    },
  },
  {
    label: 'Start Date (desc)',
    value: {
      key: 'launcher.startDate',
      order: 'desc',
    },
  },
  {
    label: 'End Date (asc)',
    value: {
      key: 'launcher.endDate',
      order: 'asc',
    },
  },
  {
    label: 'End Date (desc)',
    value: {
      key: 'launcher.endDate',
      order: 'desc',
    },
  },
  {
    label: 'Creation Date (asc)',
    value: {
      key: 'createdAt',
      order: 'asc',
    },
  },
  {
    label: 'Creation Date (desc)',
    value: {
      key: 'createdAt',
      order: 'desc',
    },
  },
];

const Promotion = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [searchKey, setSearchKey] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedSortBy, setSelectedSortBy] = useState(null);

  const resetFilter = () => {
    setSelectedStatus(null);
    setSelectedCountry(null);
    setSelectedTag(null);
    setSearchKey('');
    setStartDate(null);
    setEndDate(null);
    setDateRange('');
    setSelectedSortBy(null);
  };

  const formatRange = (selectedDates) => {
    if (selectedDates.length === 2) {
      const sDate = selectedDates[0].toISOString().split('T')[0];
      const eDate = selectedDates[1].toISOString().split('T')[0];
      setStartDate(sDate);
      setEndDate(eDate);
      return `${sDate} to ${eDate}`;
    } else {
      setStartDate(null);
      setEndDate(null);
    }
    return '';
  };

  const handleDateChange = (selectedDates) => {
    // Normalize dates to remove any time component
    const normalizedDates = selectedDates?.map(
      (date) =>
        new Date(
          Date.UTC(date?.getFullYear(), date?.getMonth(), date?.getDate())
        )
    );
    setDateRange(formatRange(normalizedDates));
  };

  document.title = 'Promotions';
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb titleInActive={'Admin'} titleActive="Promotions" />
        <Card>
          <CardHeader className="border-0">
            <Row className="g-4 align-items-center">
              <Col sm></Col>
              <Col sm="auto">
                <div className="d-flex flex-wrap align-items-start gap-2">
                  {/* add user */}
                  <button
                    className="btn btn-success add-btn"
                    onClick={() => {
                      navigate(`/promotions/detail/new`);
                    }}
                  >
                    <i className="ri-user-fill me-1 align-bottom"></i> Create
                    new promotion
                  </button>
                </div>
              </Col>
            </Row>
          </CardHeader>
          {/* Filters and search */}
          <CardBody className="border border border-dashed card-body">
            <div className="d-flex flex-row gap-2 flex-wrap">
              <div
                className="search-box d-inline-block"
                style={{ maxWidth: 450, minWidth: 450 }}
              >
                <Input
                  className="form-control search"
                  placeholder="Search by Promotion Id / Promotion Name / Coupon Code"
                  type="text"
                  value={searchKey ?? ''}
                  onChange={(event) => setSearchKey(event?.target?.value || '')}
                />
                <i className="bx bx-search-alt search-icon"></i>
              </div>

              <div>
                <Flatpickr
                  className="form-control"
                  style={{ width: 200 }}
                  options={{
                    mode: 'range',
                    dateFormat: 'Y-m-d',
                    onChange: handleDateChange,
                  }}
                  value={dateRange}
                  placeholder="Start Date to End Date"
                />
              </div>

              <div>
                <Select
                  placeholder="Select Status"
                  options={promotionStatusOptions}
                  value={selectedStatus}
                  onChange={(option) => setSelectedStatus(option)}
                />
              </div>

              <div>
                <Select
                  placeholder="Select Tags"
                  options={promotionTagsOptions}
                  value={selectedTag}
                  onChange={(option) => setSelectedTag(option)}
                />
              </div>

              <div>
                <Select
                  placeholder="Select Country"
                  options={countryOptions}
                  value={selectedCountry}
                  onChange={(option) => setSelectedCountry(option)}
                />
              </div>

              <div>
                <Select
                  placeholder="Sort By"
                  options={sortByOptions}
                  value={selectedSortBy}
                  onChange={(option) => setSelectedSortBy(option)}
                />
              </div>

              <Button
                color="light"
                outline
                className="shadow-none py-0 lh-1 d-flex align-items-center justify-content-center border-dark-subtle"
                style={{ height: 38 }}
                onClick={() => resetFilter()}
              >
                <i className="mdi mdi-restore fs-18 text-dark"></i>
              </Button>
            </div>
          </CardBody>
          <CardBody>
            <PromotionTable
              selectedCountry={selectedCountry}
              selectedStatus={selectedStatus}
              selectedTag={selectedTag}
              searchKey={searchKey}
              startDate={startDate}
              endDate={endDate}
              selectedSortBy={selectedSortBy}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default Promotion;
